'use client';

import React from 'react';

import { MapPin } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { interpolateSiteContact } from '@/lib/siteContact';

type LegalPageId =
  'privacy' | 'terms' | 'refund' | 'cookie' | 'aiUsage' | 'contentSafety' | 'security';

interface LegalSection {
  title: string;
  content: string;
}

interface LegalPageData {
  title: string;
  summary?: string;
  subtitle?: string;
  callout?: string;
  calloutHighlight?: string;
  sections: LegalSection[];
  highlights?: Array<{ label: string; desc: string }>;
  reportTitle?: string;
  reportDesc?: string;
}

interface LegalDocumentBodyProps {
  pageId: LegalPageId;
  badge?: string;
  icon?: React.ReactNode;
  calloutIcon?: React.ReactNode;
  showReportBox?: boolean;
  footerLinks?: Array<{
    href: string;
    labelKey: 'privacyLink' | 'termsLink' | 'aboutLink' | 'refundLink';
  }>;
}

function localize(text: string): string {
  return interpolateSiteContact(text);
}

export default function LegalDocumentBody({
  pageId,
  badge,
  icon,
  calloutIcon,
  showReportBox = false,
  footerLinks = [
    { href: '/privacy-policy', labelKey: 'privacyLink' },
    { href: '/terms-of-service', labelKey: 'termsLink' },
  ],
}: LegalDocumentBodyProps) {
  const { t } = useLanguage();
  const common = t?.legalPages?.common;
  const page = t?.legalPages?.[pageId] as LegalPageData | undefined;

  if (!page) {
    return null;
  }

  const displayBadge = badge ?? common?.badge ?? 'Legal';

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 pb-20">
      <div className="mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
          {icon}
          <span className="text-xs font-600 text-primary">{displayBadge}</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-800 text-foreground mb-4">{page.title}</h1>
        <p className="text-sm text-muted-foreground mb-2">
          {common?.effectiveDateLabel ?? 'Effective Date:'} {common?.effectiveDate}
        </p>

        {page.subtitle && (
          <p className="text-base text-muted-foreground mb-4 leading-relaxed">
            {localize(page.subtitle)}
          </p>
        )}

        {page.summary && (
          <div className="flex items-center gap-2 p-4 rounded-xl bg-muted/50 border border-border mt-4">
            <MapPin size={15} className="text-muted-foreground shrink-0" />
            <p className="text-sm text-muted-foreground">{localize(page.summary)}</p>
          </div>
        )}

        {page.callout && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 mt-4">
            {calloutIcon}
            <p className="text-sm text-muted-foreground">{localize(page.callout)}</p>
          </div>
        )}

        {page.highlights && page.highlights.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
            {page.highlights.map((item) => (
              <div
                key={item.label}
                className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border"
              >
                <div>
                  <p className="text-sm font-600 text-foreground mb-0.5">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-10">
        {page.sections?.map((section, i) => (
          <section
            key={`${pageId}-section-${i}`}
            id={section.title
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '')}
          >
            <h2 className="text-lg font-700 text-foreground mb-3">
              {i + 1}. {localize(section.title)}
            </h2>
            <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
              {localize(section.content)}
            </div>
          </section>
        ))}
      </div>

      {showReportBox && page.reportTitle && (
        <div className="mt-12 p-6 rounded-xl bg-primary/5 border border-primary/20">
          <p className="text-sm font-600 text-foreground mb-2">{page.reportTitle}</p>
          {page.reportDesc && (
            <p className="text-sm text-muted-foreground mb-3">{page.reportDesc}</p>
          )}
          <a
            href={`mailto:${interpolateSiteContact('{securityEmail}')}`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 border border-primary/30 text-primary text-sm font-600 hover:bg-primary/20 transition-colors"
          >
            {interpolateSiteContact('{securityEmail}')}
          </a>
        </div>
      )}
    </div>
  );
}
